import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const RESOURCE_SEARCH_FIELDS = ['name'];
const RESOURCE_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (r) => r.name?.toLowerCase() ?? '' },
];

const ResourceComponent = () => {
  const [resources, setResources] = useState([]);
  const [currentResource, setCurrentResource] = useState({ name: '' });
  const [showForm, setShowForm] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { authState } = useAuth();
  const navigate = useNavigate();

  const {
    items: visibleResources,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(resources, {
    searchFields: RESOURCE_SEARCH_FIELDS,
    sortOptions: RESOURCE_SORT_OPTIONS,
  });

  useEffect(() => {
    if (!authState) {
      navigate('/login');
    }
  }, [authState, navigate]);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    const response = await fetch(`${backendURL}/api/resources`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    const data = await response.json();
    setResources(data);
  };

  const handleFormChange = (e) => {
    setCurrentResource({ ...currentResource, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentResource.name || currentResource.name.trim() === '') {
      alert("Name can't be empty"); // Simple validation feedback
      return;
    }

    if (currentResource.resource_id) {
      await updateResource(currentResource.resource_id, currentResource);
    } else {
      await createResource(currentResource);
    }
    setCurrentResource({ name: '' });
    setShowForm(false);
    fetchResources();
  };

  const createResource = async (resourceData) => {
    await fetch(`${backendURL}/api/resources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(resourceData),
      credentials: 'include',
    });
  };

  const updateResource = async (resourceId, resourceData) => {
    await fetch(`${backendURL}/api/resources/${resourceId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(resourceData),
      credentials: 'include',
    });
  };

  const deleteResource = async () => {
    if (resourceToDelete) {
      await fetch(`${backendURL}/api/resources/${resourceToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setResourceToDelete(null);
      setShowModal(false);
      fetchResources();
    }
  };

  const editResource = (resource) => {
    setCurrentResource(resource);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentResource({ name: '' });
    setShowForm(false);
  };

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentResource({ name: '' });
    }
    setShowForm(!showForm);
  };

  const resourceForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="name">
          <Form.Label>Resource Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter resource name"
            name="name"
            onChange={handleFormChange}
            value={currentResource.name}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Resource"
        listLabel="Show Resources List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Resources"
        form={resourceForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="resource"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search resources" placeholder="Resource name" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No resources have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleResources.map((resource) => (
          <ExpandableRow
            key={resource.resource_id}
            main={{ title: resource.name }}
            actions={(
              <>
                <Button size="sm" onClick={() => editResource(resource)} aria-label="Edit resource">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setResourceToDelete(resource.resource_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete resource"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteResource}
        bodyText="Are you sure you want to delete this resource?"
      />
    </div>
  );
};

export default ResourceComponent;
